import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { dataHora, horaCurta, plural } from '../formato';
import {
  cancelarPush, chaveDoServidor, impedimentoDoPush, inscricaoAtual, inscreverNoPush,
  type ResultadoDoPush,
} from '../push';
import { useSessao } from '../sessao';
import { AVISOS_CHEGARAM, avisarQueOsAvisosMudaram } from '../SinoDeAvisos';
import { Aviso, Carregando, ErroDaApi, RespostaDaAcao, Selo, Vazio } from '../ui';
import '../avisos.css';

/**
 * Os avisos — onda 548 (Plano 2 · Comercial, Etapa 1, Tarefa 4).
 *
 * A lista do que precisa da pessoa (o sino da moldura leva aqui), as
 * preferências de cada tipo (no aplicativo, no WhatsApp, no WhatsApp fora
 * do horário comercial) e as notificações neste aparelho (o push).
 *
 * A lista anda sozinha: o sino ouve o tempo real (`usuario.<id>`) e avisa
 * esta tela (`AVISOS_CHEGARAM`); marcar lido avisa o sino
 * (`avisarQueOsAvisosMudaram`), sem esperar o servidor.
 *
 * Onda 363 (spec 13, RN-277, padrão P-AVI): os avisos do laboratório. A
 * lista ganha as abas por assunto e o «Resolvido»; as preferências, o e-mail
 * e o sino travado nos tipos do laboratório; e quem tem `avisos.configurar`
 * vê «Quem recebe cada aviso do laboratório».
 *
 * Onda 367 (RN-277, emenda): COMO cada pessoa recebe os avisos é do
 * administrador do sistema (`usuario.administrar`). O colaborador vê as dele,
 * sem mudar, e não para as notificações do aparelho; o administrador escolhe a
 * pessoa (`?pessoa=<id>`), grava e vê — e desliga — os aparelhos dela.
 */

interface AvisoDaLista {
  id: string;
  tipo: string;
  tipoNome: string;
  titulo: string;
  corpo: string;
  link: string;
  criadoEm: string;
  lidoEm: string | null;
  resolvidoEm: string | null;
  assunto: string;
}

interface Aparelho {
  id: string;
  agente: string | null;
  criadaEm: string;
  renovadaEm: string;
  falhas: number;
}

interface Pessoa { id: string; nome: string; login: string; aparelhos: number }

interface RespostaDasPreferencias {
  tipos: Preferencia[];
  temWhatsappPessoal: boolean;
  podeMudar: boolean;
  aparelhos?: Aparelho[];
}

/** O navegador e o sistema do aparelho, pelo agente de usuário — o bastante para reconhecer. */
function nomeDoAparelho(agente: string | null): string {
  if (!agente) return 'Aparelho sem identificação';
  const sistema = /Android/.test(agente) ? 'Android' : /iPhone|iPad/.test(agente) ? 'iPhone'
    : /Windows/.test(agente) ? 'Windows' : /Mac OS/.test(agente) ? 'Mac' : /Linux/.test(agente) ? 'Linux' : 'outro sistema';
  const navegador = /Edg\//.test(agente) ? 'Edge' : /OPR\//.test(agente) ? 'Opera' : /Firefox\//.test(agente) ? 'Firefox'
    : /Chrome\//.test(agente) ? 'Chrome' : /Safari\//.test(agente) ? 'Safari' : 'navegador';
  return `${navegador} no ${sistema}`;
}

interface Preferencia {
  tipo: string;
  nome: string;
  descricao: string;
  app: boolean;
  appObrigatorio: boolean;
  whatsapp: boolean;
  whatsappForaDoHorario: boolean;
  email: boolean;
}

/*
 * As abas da lista (P-AVI-03): o assunto vem do tipo (`aviso_tipo.assunto`),
 * declarado por cada módulo — a tela não tem mapa próprio. A ordem segue a
 * dos avisos (os mais novos primeiro).
 */
const TODOS = 'Todos';

/** Os tipos que ficam só no sino e no push: a preferência não liga o e-mail nem o WhatsApp (0543). */
const SO_NO_SINO = new Set(['historico_importacao']);

export function Avisos() {
  return (
    <div className="tela avisos">
      <div className="linha-topo">
        <div className="cresce">
          <h1>Avisos</h1>
          <p className="sub">
            O que precisa de você: a OS nova, as amostras que chegaram, as cápsulas para pesar, o
            que espera a sua aprovação, a conversa que passou para você. O sino do topo conta os
            que você ainda não leu.
          </p>
        </div>
      </div>
      <ListaDeAvisos />
      <NotificacoesNesteAparelho />
      <Preferencias />
      <QuemRecebe />
    </div>
  );
}

// ── A lista ──────────────────────────────────────────────────────────

function ListaDeAvisos() {
  const [dados, setDados] = useState<{ naoLidos: number; avisos: AvisoDaLista[] } | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [erroDaAcao, setErroDaAcao] = useState<unknown>(null);
  const [assunto, setAssunto] = useState<string>(TODOS);
  const [okDaAcao, setOkDaAcao] = useState<string | null>(null);
  const [sp, setSp] = useSearchParams();

  const carregar = useCallback(async () => {
    try {
      setDados(await api<{ naoLidos: number; avisos: AvisoDaLista[] }>('/avisos'));
      setErro(null);
    } catch (e) { setErro(e); }
  }, []);

  useEffect(() => {
    void carregar();
    const aoChegar = () => { void carregar(); };
    window.addEventListener(AVISOS_CHEGARAM, aoChegar);
    return () => window.removeEventListener(AVISOS_CHEGARAM, aoChegar);
  }, [carregar]);

  /*
   * Onda 364 · D7: «Lembrar em 1 h» no aviso das cápsulas. O botão da lista e a
   * ação da notificação (que abre `/avisos?adiar=<id>`: o trabalhador do
   * aparelho não tem a sessão) chegam aqui.
   */
  const adiar = useCallback(async (id: string) => {
    setErroDaAcao(null); setOkDaAcao(null);
    try {
      const r = await api<{ ate: string; naoLidos: number }>(`/avisos/${id}/adiar`, { metodo: 'POST' });
      setOkDaAcao(r.ate
        ? `Combinado: o aviso volta às ${horaCurta(r.ate)} (ou na abertura do expediente), se ainda houver cápsula sua na estufa.`
        : 'Combinado: o aviso saiu da frente.');
      avisarQueOsAvisosMudaram();
      await carregar();
    } catch (e) { setErroDaAcao(e); }
  }, [carregar]);

  const pedidoDeAdiar = sp.get('adiar');
  /* O mesmo pedido uma vez só (o StrictMode roda o efeito duas vezes em desenvolvimento). */
  const adiado = useRef<string | null>(null);
  useEffect(() => {
    if (!pedidoDeAdiar || adiado.current === pedidoDeAdiar) return;
    adiado.current = pedidoDeAdiar;
    const n = new URLSearchParams(sp);
    n.delete('adiar');
    setSp(n, { replace: true });
    void adiar(pedidoDeAdiar);
  }, [pedidoDeAdiar, sp, setSp, adiar]);

  const marcar = async (id: string) => {
    setErroDaAcao(null);
    try {
      const r = await api<{ naoLidos: number }>(`/avisos/${id}/lido`, { metodo: 'PATCH' });
      setDados((d) => d && {
        naoLidos: r.naoLidos,
        avisos: d.avisos.map((a) => (a.id === id && !a.lidoEm ? { ...a, lidoEm: new Date().toISOString() } : a)),
      });
      avisarQueOsAvisosMudaram();
    } catch (e) { setErroDaAcao(e); }
  };

  if (erro && !dados) return <ErroDaApi erro={erro} />;
  if (!dados) return <Carregando texto="Carregando os avisos" />;

  const assuntos = [...new Set(dados.avisos.map((a) => a.assunto))];
  const visiveis = assunto === TODOS ? dados.avisos : dados.avisos.filter((a) => a.assunto === assunto);

  return (
    <section className="cartao">
      <div className="cartao-cabeca">
        <h2>Os seus avisos</h2>
        <span className="sub">
          {dados.naoLidos > 0 ? plural(dados.naoLidos, 'não lido', 'não lidos') : 'Todos lidos'}
        </span>
      </div>
      <RespostaDaAcao erro={erroDaAcao} ok={okDaAcao} />
      {assuntos.length > 1 && (
        <div className="abas" role="tablist" aria-label="Assunto dos avisos">
          {[TODOS, ...assuntos].map((x) => (
            <button key={x} type="button" role="tab" aria-selected={assunto === x}
              className={assunto === x ? 'ativa' : undefined} onClick={() => setAssunto(x)}>
              {x}
            </button>
          ))}
        </div>
      )}
      {visiveis.length === 0 ? (
        <Vazio>Nenhum aviso por enquanto. Quando alguma coisa precisar de você, ela aparece aqui e no sino.</Vazio>
      ) : (
        <ul className="lista-de-avisos">
          {visiveis.map((a) => (
            <li key={a.id}
              className={`aviso-item ${a.lidoEm || a.resolvidoEm ? 'aviso-lido' : 'aviso-nao-lido'}${a.resolvidoEm ? ' aviso-resolvido' : ''}`}>
              <div className="aviso-texto">
                <Link to={a.link} onClick={() => { if (!a.lidoEm) void marcar(a.id); }}>{a.titulo}</Link>
                {a.corpo && <p className="sub">{a.corpo}</p>}
                <span className="sub">{a.tipoNome} · {dataHora(a.criadoEm)}</span>
              </div>
              <div className="aviso-acoes">
                {a.resolvidoEm
                  ? <Selo tom="ok" titulo={`Resolvido em ${dataHora(a.resolvidoEm)}`}>Resolvido</Selo>
                  : <Selo tom={a.lidoEm ? 'neutro' : 'andamento'}>{a.lidoEm ? 'Lido' : 'Não lido'}</Selo>}
                {a.tipo === 'capsulas_prontas' && !a.resolvidoEm && (
                  <button type="button" className="botao secundario adiar-aviso" onClick={() => void adiar(a.id)}>
                    Lembrar em 1 h
                  </button>
                )}
                {!a.lidoEm && !a.resolvidoEm && (
                  <button type="button" className="botao secundario marcar-lido" onClick={() => void marcar(a.id)}>
                    Marcar como lido
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── As notificações neste aparelho ───────────────────────────────────

const PASSOS_DO_IPHONE = [
  'Confira se o iPhone está no iOS 16.4 ou mais novo (Ajustes › Geral › Sobre).',
  'Abra o LabManager no Safari.',
  'Toque em Compartilhar (o quadrado com a seta para cima).',
  'Escolha «Adicionar à Tela de Início» e confirme.',
  'Abra o LabManager pelo ícone novo, entre e volte a esta tela para receber as notificações.',
];

function NotificacoesNesteAparelho() {
  const { pode } = useSessao();
  const administra = pode('usuario.administrar');
  const impedimento = impedimentoDoPush();
  const [chave, setChave] = useState<string | null | undefined>(undefined);
  const [inscrito, setInscrito] = useState(false);
  const [resultado, setResultado] = useState<ResultadoDoPush | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);

  useEffect(() => {
    if (impedimento === 'iphone_sem_tela_de_inicio' || impedimento === 'sem_suporte') return;
    void (async () => {
      try {
        setChave(await chaveDoServidor());
        setInscrito(!!(await inscricaoAtual()));
      } catch (e) { setErro(e); setChave(null); }
    })();
  }, [impedimento]);

  const ativar = async () => {
    setOcupado(true); setErro(null); setOk(null); setResultado(null);
    try {
      const r = await inscreverNoPush();
      setResultado(r);
      if (r === 'ok') { setInscrito(true); setOk('Pronto: este aparelho recebe as notificações dos avisos.'); }
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  };

  const parar = async () => {
    setOcupado(true); setErro(null); setOk(null);
    try {
      await cancelarPush();
      setInscrito(false);
      setOk('Este aparelho não recebe mais as notificações. Os avisos continuam aqui e no sino.');
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  };

  let miolo: ReactNode;
  if (impedimento === 'iphone_sem_tela_de_inicio') {
    miolo = (
      <Aviso tipo="info" titulo="No iPhone, as notificações chegam pelo LabManager na Tela de Início">
        <p>
          O iPhone só entrega notificações de um site adicionado à Tela de Início, a partir do iOS 16.4.
          Neste aparelho, faça assim:
        </p>
        <ol className="passos-do-iphone">
          {PASSOS_DO_IPHONE.map((p) => <li key={p}>{p}</li>)}
        </ol>
      </Aviso>
    );
  } else if (impedimento === 'sem_suporte') {
    miolo = (
      <Aviso tipo="info" titulo="Este navegador não recebe notificações">
        Os avisos continuam aqui e no sino. Para recebê-los com o LabManager fechado, use o Chrome,
        o Edge, o Firefox ou o Safari atualizado.
      </Aviso>
    );
  } else if (chave === undefined) {
    miolo = <Carregando texto="Carregando as notificações" />;
  } else if (chave === null) {
    miolo = (
      <Aviso tipo="info" titulo="O servidor ainda não envia notificações">
        Os avisos continuam aqui e no sino. Quando o envio for ligado, esta tela oferece receber
        neste aparelho.
      </Aviso>
    );
  } else {
    miolo = (
      <>
        <p className="sub">
          {inscrito
            ? 'Este aparelho recebe as notificações dos avisos, mesmo com o LabManager fechado.'
            : 'Receba os avisos como notificação no celular ou no computador, mesmo com o LabManager fechado.'}
        </p>
        {(resultado === 'negado' || impedimento === 'negado') && !inscrito && (
          <Aviso tipo="atencao" titulo="As notificações estão bloqueadas neste navegador">
            Sem a permissão, o aviso não chega com o LabManager fechado. Libere as notificações nas
            configurações do site (o cadeado ao lado do endereço) e toque de novo.
          </Aviso>
        )}
        <div className="acoes">
          {inscrito ? (
            /* Onda 367: parar as notificações é do administrador; o colaborador vê por quê. */
            administra ? (
              <button type="button" className="botao secundario" aria-disabled={ocupado}
                onClick={() => { if (!ocupado) void parar(); }}>
                Parar neste aparelho
              </button>
            ) : (
              <p className="sub">Para parar as notificações neste aparelho, fale com o administrador do sistema.</p>
            )
          ) : (
            /* Secundário: a ação primária da tela é «Gravar as preferências» (uma por tela). */
            <button type="button" className="botao secundario" aria-disabled={ocupado}
              onClick={() => { if (!ocupado) void ativar(); }}>
              {ocupado ? 'Ativando…' : 'Receber neste aparelho'}
            </button>
          )}
        </div>
      </>
    );
  }

  return (
    <section className="cartao">
      <div className="cartao-cabeca"><h2>Notificações neste aparelho</h2></div>
      {miolo}
      <RespostaDaAcao erro={erro} ok={ok} />
    </section>
  );
}

// ── As preferências ──────────────────────────────────────────────────

function Preferencias() {
  const { eu, pode } = useSessao();
  const administra = pode('usuario.administrar');
  const [sp, setSp] = useSearchParams();
  /* O administrador escolhe a pessoa (`?pessoa=<id>`); sem escolha, é ele mesmo. */
  const pessoa = administra ? (sp.get('pessoa') || eu?.usuarioId || '') : '';
  const [pessoas, setPessoas] = useState<Pessoa[]>([]);
  const [tipos, setTipos] = useState<Preferencia[] | null>(null);
  const [temWhatsapp, setTemWhatsapp] = useState(false);
  const [podeMudar, setPodeMudar] = useState(false);
  const [aparelhos, setAparelhos] = useState<Aparelho[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  const caminho = administra ? `/avisos/pessoas/${pessoa}/preferencias` : '/avisos/preferencias';

  const aplicar = (r: RespostaDasPreferencias) => {
    setTipos(r.tipos);
    setTemWhatsapp(r.temWhatsappPessoal);
    setPodeMudar(r.podeMudar);
    setAparelhos(r.aparelhos ?? null);
  };

  useEffect(() => {
    if (!administra) return;
    void (async () => {
      try { setPessoas((await api<{ pessoas: Pessoa[] }>('/avisos/pessoas')).pessoas); } catch (e) { setErro(e); }
    })();
  }, [administra]);

  useEffect(() => {
    if (administra && !pessoa) return;
    setTipos(null); setOk(null); setErro(null);
    void (async () => {
      try { aplicar(await api<RespostaDasPreferencias>(caminho)); } catch (e) { setErro(e); }
    })();
  }, [administra, pessoa, caminho]);

  const escolherPessoa = (id: string) => {
    const n = new URLSearchParams(sp);
    if (id && id !== eu?.usuarioId) n.set('pessoa', id); else n.delete('pessoa');
    setSp(n, { replace: true });
  };

  const desligar = async (a: Aparelho) => {
    setErro(null); setOk(null);
    try {
      const r = await api<{ aparelhos: Aparelho[] }>(`/avisos/pessoas/${pessoa}/aparelhos/${a.id}`, { metodo: 'DELETE' });
      setAparelhos(r.aparelhos);
      setPessoas((l) => l.map((x) => (x.id === pessoa ? { ...x, aparelhos: r.aparelhos.length } : x)));
      setOk(`${nomeDoAparelho(a.agente)} não recebe mais as notificações desta pessoa.`);
    } catch (e) { setErro(e); }
  };

  const mudar = (tipo: string, campo: 'app' | 'whatsapp' | 'whatsappForaDoHorario' | 'email', valor: boolean) => {
    if (!podeMudar) return;
    setOk(null);
    setTipos((l) => l && l.map((t) => {
      if (t.tipo !== tipo) return t;
      const novo = { ...t, [campo]: valor };
      if (campo === 'whatsapp' && !valor) novo.whatsappForaDoHorario = false;
      return novo;
    }));
  };

  const gravar = async () => {
    if (!tipos || gravando) return;
    setGravando(true); setErro(null); setOk(null);
    try {
      const r = await api<RespostaDasPreferencias>(caminho, {
        metodo: 'PUT',
        corpo: { preferencias: tipos.map(({ tipo, app, whatsapp, whatsappForaDoHorario, email }) => ({ tipo, app, whatsapp, whatsappForaDoHorario, email })) },
      });
      aplicar(r);
      const nome = pessoas.find((x) => x.id === pessoa)?.nome;
      setOk(nome && pessoa !== eu?.usuarioId ? `Preferências de ${nome} gravadas.` : 'Preferências gravadas.');
    } catch (e) { setErro(e); } finally { setGravando(false); }
  };

  const outra = administra && pessoa !== eu?.usuarioId;
  const nomeDaPessoa = pessoas.find((x) => x.id === pessoa)?.nome ?? 'esta pessoa';

  const escolha = administra && (
    <div className="campo pref-pessoa">
      <label htmlFor="pref-pessoa">Pessoa</label>
      <select id="pref-pessoa" value={pessoa} onChange={(e) => escolherPessoa(e.target.value)}>
        {!pessoas.some((x) => x.id === eu?.usuarioId) && eu && <option value={eu.usuarioId}>Eu</option>}
        {pessoas.map((x) => (
          <option key={x.id} value={x.id}>
            {x.id === eu?.usuarioId ? `${x.nome} (eu)` : x.nome}
            {x.aparelhos === 0 ? ' · sem aparelho' : ''}
          </option>
        ))}
      </select>
    </div>
  );

  if (erro && !tipos) return (
    <section className="cartao">
      <div className="cartao-cabeca"><h2>Como receber cada aviso</h2></div>
      {escolha}
      <ErroDaApi erro={erro} />
    </section>
  );
  if (!tipos) return <Carregando texto="Carregando as preferências" />;

  return (
    <section className="cartao">
      <div className="cartao-cabeca"><h2>Como receber cada aviso</h2></div>
      <p className="sub">
        No aplicativo é o sino, esta lista e a notificação no aparelho. Os avisos do laboratório
        ficam sempre no sino; o e-mail e o WhatsApp se escolhem em cada linha. Fora do horário
        comercial, o WhatsApp fica em silêncio, a não ser que esteja marcado o contrário.
      </p>
      {administra ? (
        <>
          {escolha}
          <p className="sub">
            Só o administrador do sistema ajusta como cada pessoa recebe os avisos, para ninguém
            desligar um aviso importante. Cada mudança vai à trilha.
          </p>
        </>
      ) : (
        <Aviso tipo="info" titulo="Quem ajusta como você recebe os avisos é o administrador do sistema">
          Assim nenhum aviso importante fica desligado. Para mudar o e-mail ou o WhatsApp de algum
          aviso, fale com ele.
        </Aviso>
      )}
      {!temWhatsapp && (
        <Aviso tipo="info" titulo={outra ? `O WhatsApp pessoal de ${nomeDaPessoa} ainda não está no cadastro`
          : 'O seu WhatsApp pessoal ainda não está no cadastro'}>
          Os avisos pelo WhatsApp começam a chegar quando ele for cadastrado na ficha de funcionário.
          Até lá, vale o que estiver marcado no aplicativo.
        </Aviso>
      )}
      <div className="rola">
      <table className="dados preferencias-de-aviso">
        <thead>
          <tr>
            <th scope="col">Aviso</th>
            <th scope="col">No aplicativo</th>
            <th scope="col">Por e-mail</th>
            <th scope="col">No WhatsApp</th>
            <th scope="col">WhatsApp fora do horário</th>
          </tr>
        </thead>
        <tbody>
          {tipos.map((t) => (
            <tr key={t.tipo}>
              <th scope="row">
                <span>{t.nome}</span>
                <span className="sub">{t.descricao}</span>
              </th>
              <td>
                <label className="pref-marcador">
                  {/* P-UI: nada some nem fica mudo — o marcador fixo diz por quê (aria-disabled, não disabled). */}
                  <input type="checkbox" checked={t.app} aria-disabled={t.appObrigatorio || !podeMudar || undefined}
                    aria-label={`${t.nome}: no aplicativo${t.appObrigatorio ? ' (sempre)' : ''}`}
                    onChange={(e) => { if (!t.appObrigatorio) mudar(t.tipo, 'app', e.target.checked); }} />
                  {t.appObrigatorio && <span className="sub pref-sempre">sempre no sino</span>}
                </label>
              </td>
              {SO_NO_SINO.has(t.tipo) ? (
                /* 0543: o aviso da importação do histórico não vai por e-mail nem pelo WhatsApp — o texto diz por quê. */
                <td colSpan={3}><span className="sub">só no sino e no push (nunca por e-mail nem pelo WhatsApp)</span></td>
              ) : (<>
              <td>
                <label className="pref-marcador">
                  <input type="checkbox" checked={t.email} aria-label={`${t.nome}: por e-mail`}
                    aria-disabled={!podeMudar || undefined}
                    onChange={(e) => mudar(t.tipo, 'email', e.target.checked)} />
                </label>
              </td>
              <td>
                <label className="pref-marcador">
                  <input type="checkbox" checked={t.whatsapp} aria-label={`${t.nome}: no WhatsApp`}
                    aria-disabled={!podeMudar || undefined}
                    onChange={(e) => mudar(t.tipo, 'whatsapp', e.target.checked)} />
                </label>
              </td>
              <td>
                <label className="pref-marcador">
                  <input type="checkbox" checked={t.whatsappForaDoHorario} disabled={!t.whatsapp}
                    aria-disabled={!podeMudar || undefined}
                    aria-label={`${t.nome}: no WhatsApp fora do horário comercial`}
                    onChange={(e) => mudar(t.tipo, 'whatsappForaDoHorario', e.target.checked)} />
                </label>
              </td>
              </>)}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {aparelhos && (
        <div className="pref-aparelhos">
          <h3>Aparelhos que recebem as notificações</h3>
          {aparelhos.length === 0 ? (
            <Aviso tipo="atencao" titulo={outra ? `${nomeDaPessoa} não recebe notificação em nenhum aparelho`
              : 'Você não recebe notificação em nenhum aparelho'}>
              Os avisos ficam no sino e na lista, mas não chegam com o LabManager fechado.
              {outra ? ` Peça a ${nomeDaPessoa} que entre com a conta dela no celular ou no computador e toque em «Receber neste aparelho».`
                : ' No celular ou no computador, entre com a sua conta e toque em «Receber neste aparelho».'}
            </Aviso>
          ) : (
            <ul className="lista-de-aparelhos">
              {aparelhos.map((a) => (
                <li key={a.id}>
                  <span className="cresce">
                    <strong>{nomeDoAparelho(a.agente)}</strong>
                    <span className="sub"> · última entrada em {dataHora(a.renovadaEm)}
                      {a.falhas > 0 ? ` · ${plural(a.falhas, 'recusa', 'recusas')} do serviço de push` : ''}</span>
                  </span>
                  <button type="button" className="botao discreto" onClick={() => void desligar(a)}
                    aria-label={`Parar as notificações deste aparelho: ${nomeDoAparelho(a.agente)}`}>
                    Parar as notificações deste aparelho
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <RespostaDaAcao erro={erro} ok={ok} />
      {podeMudar && (
        <div className="acoes">
          <button type="button" className="botao" aria-disabled={gravando} onClick={() => void gravar()}>
            {gravando ? 'Gravando…' : outra ? `Gravar as preferências de ${nomeDaPessoa}` : 'Gravar as preferências'}
          </button>
        </div>
      )}
    </section>
  );
}

// ── Quem recebe cada aviso do laboratório (onda 363, spec 13 §3) ─────

interface TipoDeDestino {
  tipo: string;
  nome: string;
  descricao: string;
  destino: 'perfil' | 'autor' | 'aprovador';
  emailPadrao: boolean;
  perfis: string[];
}

const REGRA_FIXA: Record<string, string> = {
  autor: 'Só quem lançou: regra fixa.',
  aprovador: 'Todos que podem aprovar, menos quem a segregação proíbe: regra fixa.',
};

function QuemRecebe() {
  const { pode } = useSessao();
  const configura = pode('avisos.configurar');
  const [tipos, setTipos] = useState<TipoDeDestino[] | null>(null);
  const [perfis, setPerfis] = useState<{ codigo: string; nome: string }[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);

  useEffect(() => {
    if (!configura) return;
    void (async () => {
      try {
        const r = await api<{ tipos: TipoDeDestino[]; perfis: { codigo: string; nome: string }[] }>('/avisos/destinos');
        setTipos(r.tipos); setPerfis(r.perfis);
      } catch (e) { setErro(e); }
    })();
  }, [configura]);

  if (!configura) return null;
  if (erro && !tipos) return <ErroDaApi erro={erro} />;
  if (!tipos) return <Carregando texto="Carregando quem recebe cada aviso" />;

  const alternar = (tipo: string, perfil: string) => {
    setOk(null);
    setTipos((l) => l && l.map((t) => (t.tipo !== tipo ? t : {
      ...t, perfis: t.perfis.includes(perfil) ? t.perfis.filter((p) => p !== perfil) : [...t.perfis, perfil],
    })));
  };
  const email = (tipo: string, valor: boolean) => {
    setOk(null);
    setTipos((l) => l && l.map((t) => (t.tipo !== tipo ? t : { ...t, emailPadrao: valor })));
  };

  const gravar = async () => {
    if (gravando) return;
    setGravando(true); setErro(null); setOk(null);
    try {
      const r = await api<{ tipos: TipoDeDestino[] }>('/avisos/destinos', {
        metodo: 'PUT',
        corpo: {
          tipos: tipos.map((t) => (t.destino === 'perfil'
            ? { tipo: t.tipo, perfis: t.perfis, emailPadrao: t.emailPadrao }
            : { tipo: t.tipo, emailPadrao: t.emailPadrao })),
        },
      });
      setTipos(r.tipos);
      setOk('Quem recebe cada aviso foi gravado.');
    } catch (e) { setErro(e); } finally { setGravando(false); }
  };

  return (
    <section className="cartao quem-recebe">
      <div className="cartao-cabeca"><h2>Quem recebe cada aviso do laboratório</h2></div>
      <p className="sub">
        Os perfis marcados recebem o aviso no sino e, com o push ligado, no celular e no computador.
        O e-mail padrão vale para quem não escolheu outra coisa nas preferências.
      </p>
      <div className="rola">
      <table className="dados quem-recebe-tabela">
        <thead>
          <tr>
            <th scope="col">Aviso</th>
            <th scope="col">Quem recebe</th>
            <th scope="col">E-mail por padrão</th>
          </tr>
        </thead>
        <tbody>
          {tipos.map((t) => (
            <tr key={t.tipo}>
              <th scope="row">
                <span>{t.nome}</span>
                <span className="sub">{t.descricao}</span>
              </th>
              <td>
                {t.destino === 'perfil' ? (
                  <fieldset className="quem-recebe-perfis">
                    <legend className="so-leitor">Perfis que recebem «{t.nome}»</legend>
                    {perfis.map((p) => (
                      <label key={p.codigo} className="pref-marcador">
                        <input type="checkbox" checked={t.perfis.includes(p.codigo)}
                          onChange={() => alternar(t.tipo, p.codigo)} />
                        <span>{p.nome}</span>
                      </label>
                    ))}
                  </fieldset>
                ) : (
                  <span className="sub">
                    {REGRA_FIXA[t.destino]}
                    {t.tipo === 'capsulas_prontas' && (
                      <> As horas de estufa ficam em <Link to="/ajustes">Ajustes do sistema</Link>.</>
                    )}
                  </span>
                )}
              </td>
              <td>
                <label className="pref-marcador">
                  <input type="checkbox" checked={t.emailPadrao} aria-label={`${t.nome}: e-mail por padrão`}
                    onChange={(e) => email(t.tipo, e.target.checked)} />
                </label>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <RespostaDaAcao erro={erro} ok={ok} />
      <div className="acoes">
        {/* Secundário: a primária da tela é «Gravar as preferências» (uma por tela). */}
        <button type="button" className="botao secundario" aria-disabled={gravando} onClick={() => void gravar()}>
          {gravando ? 'Gravando…' : 'Gravar quem recebe'}
        </button>
      </div>
    </section>
  );
}
